import * as React from "react";
import { ApiKey, ApiKeysService } from "gen/clients";
import { useDialogState } from "hooks/useDialogState";
import { useMutation, useQuery } from "react-query";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { CreateApiKeyPanel } from "./CreateApiKeyPanel";
import { EditApiKeyPanel } from "./EditApiKeyPanel";
import { MtlsCertificatesPanel } from "./MtlsCertificatesPanel/MtlsCertificatesPanel";
import { formatDate } from "../../../../../utils/dateUtils";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  clientId: string;
  onSaveSuccess: () => void;
}

const ApiKeysTab: React.FC<Props> = ({ clientId, onSaveSuccess }) => {
  const isReadOnly = useClientReadOnly();
  const [isCreatePanelOpen, openCreatePanel, closeCreatePanel] = useDialogState();
  const [isEditPanelOpen, openEditPanel, closeEditPanel] = useDialogState();
  const [isCertsPanelOpen, openCertsPanel, closeCertsPanel] = useDialogState();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [selectedApiKey, setSelectedApiKey] = React.useState<ApiKey | undefined>();

  const {
    data: apiKeys,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(["listApiKeys", clientId], {
    queryFn: () => ApiKeysService.listApiKeys({ clientId }),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const {
    mutate: deleteApiKey,
    isLoading: isDeleting,
    error: deleteError,
    reset: resetDeleteApiKey
  } = useMutation(ApiKeysService.deleteApiKey, {
    onSuccess: () => {
      closeDeleteDialog();
      refetch();
      onSaveSuccess();
    }
  });

  const onCreateSuccess = React.useCallback(() => {
    closeCreatePanel();
    refetch();
    onSaveSuccess();
  }, [closeCreatePanel, refetch, onSaveSuccess]);

  const onEditSuccess = React.useCallback(() => {
    closeEditPanel();
    refetch();
    onSaveSuccess();
  }, [closeEditPanel, refetch, onSaveSuccess]);

  const onEditClick = React.useCallback(
    (apiKey: ApiKey) => {
      setSelectedApiKey(apiKey);
      openEditPanel();
    },
    [openEditPanel]
  );

  const onManageCertificatesClick = React.useCallback(
    (apiKey: ApiKey) => {
      setSelectedApiKey(apiKey);
      openCertsPanel();
    },
    [openCertsPanel]
  );

  const onDeleteClick = React.useCallback(
    (apiKey: ApiKey) => {
      resetDeleteApiKey();
      setSelectedApiKey(apiKey);
      openDeleteDialog();
    },
    [openDeleteDialog, resetDeleteApiKey]
  );

  const onDeleteConfirm = React.useCallback(() => {
    if (selectedApiKey) {
      deleteApiKey({ keyId: selectedApiKey.id });
    }
  }, [deleteApiKey, selectedApiKey]);

  const nameCell = React.useCallback((row: ApiKey) => {
    return <Typography variant="body2">{row.name}</Typography>;
  }, []);

  const descriptionCell = React.useCallback((row: ApiKey) => {
    return <Typography variant="body2">{row.description || "-"}</Typography>;
  }, []);

  const createdAtCell = React.useCallback((row: ApiKey) => {
    return <Typography variant="body2">{formatDate(new Date(row.createdAt))}</Typography>;
  }, []);

  const lastSixCharsCell = React.useCallback((row: ApiKey) => {
    return (
      <Typography variant="body2" fontFamily="monospace">
        {row.lastSixChars ? `...${row.lastSixChars}` : "-"}
      </Typography>
    );
  }, []);

  const statusCell = React.useCallback((row: ApiKey) => {
    return (
      <Typography variant="body2" color={row.enabled ? "success.main" : "error.main"}>
        {row.enabled ? "Active" : "Inactive"}
      </Typography>
    );
  }, []);

  const rateLimitCell = React.useCallback((row: ApiKey) => {
    return <Typography variant="body2">{row.rateLimit || "-"}</Typography>;
  }, []);

  const quotaLimitCell = React.useCallback((row: ApiKey) => {
    if (!row.quotaLimit) {
      return <Typography variant="body2">-</Typography>;
    }

    const periodLabel = row.quotaPeriod ? ` / ${row.quotaPeriod.toLowerCase()}` : " / month";

    return (
      <Typography variant="body2">
        {row.quotaLimit}
        {periodLabel}
      </Typography>
    );
  }, []);

  const mtlsCell = React.useCallback((row: ApiKey) => {
    return (
      <Typography variant="body2" color={row.mtls?.required ? "success.main" : "text.secondary"}>
        {row.mtls?.required ? "Required" : "Not required"}
      </Typography>
    );
  }, []);

  const rowActions = React.useCallback(
    (apiKey: ApiKey): DataTableRowAction[] => [
      {
        title: "Edit",
        action: () => onEditClick(apiKey),
        disabled: isReadOnly
      },
      {
        title: "Manage Certificates",
        action: () => onManageCertificatesClick(apiKey),
        disabled: !apiKey.mtls?.required || isReadOnly
      },
      {
        title: "Delete",
        action: () => onDeleteClick(apiKey),
        color: "error.main",
        disabled: isReadOnly
      }
    ],
    [onEditClick, onManageCertificatesClick, onDeleteClick, isReadOnly]
  );

  return (
    <>
      <Stack spacing={2}>
        <Box>
          <Button variant="contained" color="primary" onClick={openCreatePanel} disabled={isReadOnly}>
            Add API Key
          </Button>
        </Box>
        <DataTable
          columns={[
            {
              id: "name",
              title: <strong>Name</strong>,
              cell: nameCell
            },
            {
              id: "description",
              title: <strong>Description</strong>,
              cell: descriptionCell
            },
            {
              id: "lastSixChars",
              title: <strong>Key Preview</strong>,
              cell: lastSixCharsCell
            },
            {
              id: "rateLimit",
              title: <strong>Rate Limit</strong>,
              cell: rateLimitCell
            },
            {
              id: "quotaLimit",
              title: <strong>Quota Limit</strong>,
              cell: quotaLimitCell
            },
            {
              id: "createdAt",
              title: <strong>Created</strong>,
              cell: createdAtCell
            },
            {
              id: "status",
              title: <strong>Status</strong>,
              cell: statusCell
            },
            {
              id: "mtls",
              title: <strong>mTLS</strong>,
              cell: mtlsCell
            }
          ]}
          data={apiKeys?.items || []}
          rowKey={(row: ApiKey) => row.id}
          rowActions={rowActions}
          isLoading={isLoading}
          error={isError && error ? <ApiErrorMessage apiError={error} /> : undefined}
        />
      </Stack>

      <CreateApiKeyPanel
        open={isCreatePanelOpen}
        clientId={clientId}
        onClose={closeCreatePanel}
        onSuccess={onCreateSuccess}
      />

      {selectedApiKey && (
        <EditApiKeyPanel
          open={isEditPanelOpen}
          apiKey={selectedApiKey}
          onClose={closeEditPanel}
          onSuccess={onEditSuccess}
        />
      )}

      {selectedApiKey && (
        <MtlsCertificatesPanel
          open={isCertsPanelOpen}
          apiKey={selectedApiKey}
          onClose={closeCertsPanel}
          onSaveSuccess={onSaveSuccess}
        />
      )}

      {selectedApiKey && isDeleteDialogOpen && (
        <ConfirmationDialog
          title="Delete API Key"
          content={
            <div>
              Are you sure you want to delete the API key <strong>{selectedApiKey.name}</strong>? This action cannot be
              undone.
            </div>
          }
          onClose={closeDeleteDialog}
          onConfirm={onDeleteConfirm}
          confirmationButtonColor="error"
          isLoading={isDeleting}
          error={deleteError ? <ApiErrorMessage apiError={deleteError} /> : undefined}
        />
      )}
    </>
  );
};

export { ApiKeysTab };
